import type { HTMLAttributes, PropsWithChildren } from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';
import type { TextStyleValue } from '@elfsight/configurator-contracts';
import type { WithSX } from '@elfsight/lib-utils';
import { applySX } from '@elfsight/lib-utils';
import { getLineHeight } from '@elfsight/widget-utils';
import type { TypographyToken } from './typography-tokens';
import { getTokenFontStyle } from './typography-tokens';

export type Never<T> = {
  [K in keyof T]: never;
};

// TODO: Remove duplicated lineHeight in props and textStyle
export type TextStyleWithLineHeight = TextStyleValue & {
  lineHeight: string | number;
};

export type BaseTypographyProps = HTMLAttributes<HTMLDivElement> &
  PropsWithChildren<{
    color?: string;
    lineHeight?: number | string;
    display?: string;
  }>;

export type TypographyTextStyleProps = {
  textStyle?: Partial<TextStyleWithLineHeight>;
};

export type TypographyTokensProps = {
  textToken: TypographyToken;
  textTransform?: TextStyleWithLineHeight['textTransform'];
  textAlign?: TextStyleWithLineHeight['textAlign'];
};

export type TypographyTextDiscriminatingProps =
  | (TypographyTextStyleProps & Partial<Never<TypographyTokensProps>>)
  | (TypographyTokensProps & Partial<Never<TypographyTextStyleProps>>);

export type TypographyProps = BaseTypographyProps &
  TypographyTextDiscriminatingProps;

export const Typography = forwardRef<HTMLDivElement, TypographyProps>(
  function Typography(
    {
      textToken,
      textStyle: derivedTextStyle,
      textTransform,
      textAlign,

      lineHeight,
      children,
      color = 'currentColor',
      display = 'block',
      ...forwardingProps
    },
    forwardingRef
  ) {
    const textStyle = getTextStyle(
      textToken !== undefined
        ? { textToken, textTransform, textAlign, lineHeight }
        : { textStyle: derivedTextStyle, lineHeight }
    );

    return (
      <Container
        $sx={{
          color,
          display,
          textTransform: textStyle.textTransform || 'inherit',
          textAlign: textStyle.textAlign || 'inherit',
          fontSize: textStyle.fontSize || 'inherit',
          fontStyle: textStyle.fontStyle || 'normal',
          fontWeight: textStyle.fontWeight || 'normal',
          lineHeight: textStyle.lineHeight || 'normal',
          a: { color }
        }}
        {...forwardingProps}
        ref={forwardingRef}
      >
        {children}
      </Container>
    );
  }
);

const Container = styled.div<WithSX>`
  ${applySX};
`;

export function arePropsTokenized(
  props: TypographyTextDiscriminatingProps
): props is TypographyTokensProps & Never<TypographyTextStyleProps> {
  return 'textToken' in props;
}

export function getTextStyle(
  props: TypographyTextDiscriminatingProps & Pick<TypographyProps, 'lineHeight'>
): Partial<TextStyleWithLineHeight> {
  if (arePropsTokenized(props)) {
    return getTokenFontStyle(props.textToken);
  }

  const { textStyle = {}, lineHeight } = props;

  return {
    ...textStyle,
    lineHeight: getLineHeight({
      ...textStyle,
      lineHeight: lineHeight ?? textStyle.lineHeight
    })
  };
}
